import { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../context/AuthContext';
import { registerFcmToken } from '../api/atticApi';

// Mount once at the top of the auth-aware tree (alongside NativeDeepLinkBridge).
// On native: requests notification permission, kicks off FCM registration,
// listens for the token, and POSTs it to /RegisterFcmToken once the user is
// authenticated. Renders nothing.
//
// Single-token model — Customer.fcm_token gets overwritten on each register.
// Repeat POSTs with the same (email, token) pair are gated client-side and
// deduped server-side.
export function NativePushBridge() {
  const { isAuthenticated, customerEmail } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  const sentKeyRef = useRef<string | null>(null);

  // Native-only one-time setup: permissions + register + listeners.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cancelled = false;
    let regHandle: { remove: () => Promise<void> } | undefined;
    let errHandle: { remove: () => Promise<void> } | undefined;

    (async () => {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const status = await PushNotifications.checkPermissions();
        let granted = status.receive === 'granted';
        if (status.receive === 'prompt' || status.receive === 'prompt-with-rationale') {
          const requested = await PushNotifications.requestPermissions();
          granted = requested.receive === 'granted';
        }
        if (!granted || cancelled) return;

        regHandle = await PushNotifications.addListener('registration', (event) => {
          if (cancelled) return;
          setToken(event.value);
        });
        errHandle = await PushNotifications.addListener('registrationError', (err) => {
          console.warn('[push] registrationError', err);
        });
        await PushNotifications.register();
      } catch (err) {
        console.warn('[push] setup failed', err);
      }
    })();

    return () => {
      cancelled = true;
      void regHandle?.remove();
      void errHandle?.remove();
    };
  }, []);

  // POST token whenever both auth and token are ready (and we haven't already
  // POSTed this exact pair). Re-POSTs after sign-out → sign-in-as-different
  // user because the (email, token) key changes.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (!isAuthenticated || !customerEmail || !token) return;
    const key = `${customerEmail}|${token}`;
    if (sentKeyRef.current === key) return;
    sentKeyRef.current = key;
    void registerFcmToken({ token }).catch((err) => {
      console.warn('[push] token registration failed', err);
      // Allow retry next time auth/token changes.
      sentKeyRef.current = null;
    });
  }, [isAuthenticated, customerEmail, token]);

  return null;
}
